import { User, Role } from "@/types/user";

export type RoleLabel = "Admin" | "User" | "Assistant" | "Advisor";

// Role hierarchy from lowest to highest priority
const ROLE_HIERARCHY = [
  Role.USER,
  Role.ASSISTANT,
  Role.ADVISOR,
  Role.FIRMADMIN,
  Role.ADMIN,
] as const;

// Role to display label mapping
const ROLE_LABELS: Record<Role, RoleLabel> = {
  [Role.USER]: "User",
  [Role.ASSISTANT]: "Assistant",
  [Role.ADVISOR]: "Advisor",
  [Role.FIRMADMIN]: "Admin",
  [Role.ADMIN]: "Admin", // Internal role, displays same as FIRMADMIN
} as const;

/**
 * Determines the highest role a user has and returns its display label
 * @param user - The user object with roles
 * @returns The display label for the user's highest role
 */
export function getUserRoleDisplay(user: User): RoleLabel {
  if (!user.roles || user.roles.length === 0) {
    return "User"; // Default to User if no roles
  }

  // Find the highest role in the hierarchy
  let highestRole = Role.USER;
  let highestPriority = -1;

  for (const roleAssignment of user.roles) {
    const priority = ROLE_HIERARCHY.indexOf(roleAssignment.role);
    if (priority > highestPriority) {
      highestPriority = priority;
      highestRole = roleAssignment.role;
    }
  }

  return ROLE_LABELS[highestRole];
}

export function isUserAdmin(roles?: Role[]) {
  if (!roles?.length) return false;
  return roles.includes(Role.ADMIN) || roles.includes(Role.FIRMADMIN);
}
